<template>
  <v-container id="business_account_root" fluid>
    <!-- CONTENT -->
    <v-container fluid>
      <!-- Back button (admin mode) -->
      <v-row v-if="fromAdmin" class="mb-2 px-6">
        <v-col>
          <v-btn text color="primary" @click="$router.push({ name: 'BusinessAccountAdmin' })">
            <v-icon left>mdi-arrow-left</v-icon>
            {{ $t('businessAccount.admin.back') }}
          </v-btn>
        </v-col>
      </v-row>

      <!-- Loading -->
      <v-row v-if="businessAccountLoading">
        <v-col class="d-flex justify-center">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </v-col>
      </v-row>

      <template v-if="!businessAccountLoading && businessAccountDetails">
        <!-- BA DETAILS SUMMARY -->
        <v-row id="business-account-details">
          <v-col>
            <v-card class="pa-6 mb-4">
              <v-card-title>
                <v-icon color="black" class="mr-2">mdi-information-outline</v-icon>
                {{ $t("businessAccount.fields.details") }}
              </v-card-title>
              <v-list-item>
                <v-list-item-content>
                  <v-simple-table dense>
                    <template #default>
                      <tbody>
                        <tr>
                          <td>
                            <strong>{{ businessAccountDetails.name }}</strong>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{{ $t("businessAccount.fields.id") }}</strong>
                          </td>
                          <td>{{ businessAccountDetails.id }}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{{ $t("businessAccount.fields.createdAt") }}</strong>
                          </td>
                          <td>{{ formatDate(businessAccountDetails.createdAt) }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-list-item-content>
              </v-list-item>

              <!-- Admin: inline name edit -->
              <v-row v-if="isAdmin" class="mt-2 px-4" align="center">
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editName"
                    :label="$t('businessAccount.admin.name')"
                    outlined
                    dense
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    color="primary"
                    small
                    :disabled="!editName || editName === businessAccountDetails.name || savingName"
                    :loading="savingName"
                    @click="submitNameUpdate"
                  >
                    {{ $t('main.save') }}
                  </v-btn>
                </v-col>
              </v-row>

              <v-btn
                id="ba_refresh"
                text
                color="primary"
                class="mt-4"
                @click="loadBusinessAccountDetails(businessAccountDetails.id)"
              >
                <v-icon>mdi-refresh</v-icon>
                {{ $t("businessAccount.refresh") }}
              </v-btn>
            </v-card>
          </v-col>
        </v-row>

        <!-- MEMBERS -->
        <v-row>
          <v-col>
            <v-card class="pa-6 mb-4">
              <v-card-title>
                <v-icon color="black" class="mr-2">mdi-account-multiple</v-icon>
                {{ $t("businessAccount.members.title") }}
              </v-card-title>

              <!-- Add Member -->
              <fieldset
                class="pa-4 mb-4"
                style="border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 4px"
              >
                <legend class="px-2" style="font-size: 16px; font-weight: 500">
                  {{ $t("businessAccount.members.addNewMember") }}
                </legend>
                <v-row align="center">
                  <v-col cols="12" md="8">
                    <v-text-field
                      id="member_email"
                      v-model="newMemberEmail"
                      :label="$t('businessAccount.members.email')"
                      :placeholder="$t('businessAccount.members.emailPlaceholder')"
                      prepend-inner-icon="mdi-email"
                      :rules="[rules.email]"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-switch
                      id="member_is_admin"
                      v-model="newMemberIsAdmin"
                      :label="$t('businessAccount.members.isAdmin')"
                      hide-details
                    ></v-switch>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-btn
                      id="add_member_btn"
                      color="primary"
                      :disabled="!newMemberEmail || addingMember"
                      :loading="addingMember"
                      @click="addMember"
                    >
                      <v-icon left>mdi-account-plus</v-icon>
                      {{ $t("businessAccount.members.addMember") }}
                    </v-btn>
                  </v-col>
                </v-row>
              </fieldset>

              <!-- Members Table -->
              <v-data-table
                id="ba_members_table"
                item-key="id"
                class="elevation-1"
                must-sort
                :headers="businessAccountMembersHeaders"
                :items="businessAccountMembers"
                :options.sync="membersPagination"
                :server-items-length="membersTotalItems"
                :loading="businessAccountMembersLoading"
                :no-data-text="$t('businessAccount.members.noData')"
                :loading-text="$t('dataTable.content.loadingPleaseWait')"
                :footer-props="{
                  'items-per-page-text': $t('dataTable.footer.itemsPerPageText'),
                  'page-text': $t('dataTable.footer.pageText'),
                  'items-per-page-options': [5, 10, 25, 50],
                }"
              >
                <template #item.businessAccountRole="{ item }">
                  <v-chip
                    small
                    :color="item.businessAccountRole === 'ADMIN' ? 'primary' : 'default'"
                    :disabled="item.businessAccountStatus === 'INVITED' || updatingMemberRole"
                    style="cursor: pointer"
                    @click="toggleMemberRole(item)"
                  >
                    <v-icon small left>
                      {{ item.businessAccountRole === "ADMIN" ? "mdi-shield-account" : "mdi-account" }}
                    </v-icon>
                    {{
                      item.businessAccountRole === "ADMIN"
                        ? $t("businessAccount.members.admin")
                        : $t("businessAccount.members.member")
                    }}
                  </v-chip>
                </template>
                <template #item.businessAccountStatus="{ item }">
                  {{
                    item.businessAccountStatus === "ACTIVE"
                      ? $t("businessAccount.members.statusActive")
                      : item.businessAccountStatus === "INVITED"
                        ? $t("businessAccount.members.statusInvited")
                        : ""
                  }}
                </template>
                <template #item.actions="{ item }">
                  <v-btn
                    id="delete_member_btn"
                    icon
                    small
                    color="error"
                    :disabled="deletingMember"
                    @click="deleteMember(item, $event)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>

        <!-- USER GROUPS -->
        <v-row id="user-groups-table">
          <v-col>
            <v-card class="pa-6 mb-4">
              <v-card-title>
                <v-icon color="black" class="mr-2">mdi-account-group</v-icon>
                {{ $t("userGroups.title") }}
              </v-card-title>
              <user-groups :ba-id="businessAccountDetails.id"></user-groups>
            </v-card>
          </v-col>
        </v-row>

        <!-- PROVIDER LIMIT CONFIGURATIONS -->
        <v-row
          v-if="businessAccountDetails.providerLimitConfigs && businessAccountDetails.providerLimitConfigs.length"
          id="provider-limit-table"
        >
          <v-col>
            <v-card class="pa-6 mb-4">
              <v-card-title>
                <v-icon color="black" class="mr-2">mdi-tune</v-icon>
                {{ $t("businessAccount.providerConfig.title") }}
                <v-spacer></v-spacer>
                <!-- Update limits button - admin only -->
                <v-btn
                  v-if="isAdmin"
                  id="update_limit_button"
                  color="primary"
                  small
                  @click="openUpdateDialog"
                >
                  <v-icon small left>mdi-pencil</v-icon>
                  {{ $t('businessAccount.admin.updateLimits') }}
                </v-btn>
              </v-card-title>

              <v-tabs
                v-model="activeProviderTab"
                background-color="transparent"
                show-arrows
              >
                <v-tab
                  v-for="provider in businessAccountDetails.providerLimitConfigs"
                  :id="provider.providerType + '-TAB'"
                  :key="provider.id"
                >
                  {{ provider.providerType }}
                  <span v-if="provider.providerKind">&nbsp;({{ provider.providerKind }})</span>
                </v-tab>
              </v-tabs>

              <v-divider></v-divider>

              <v-tabs-items v-model="activeProviderTab">
                <v-tab-item
                  v-for="provider in businessAccountDetails.providerLimitConfigs"
                  :key="provider.id"
                >
                  <v-card flat>
                    <v-card-text>
                      <v-row class="mb-2">
                        <v-col cols="12" sm="4">
                          <div class="caption grey--text text--darken-1">
                            {{ $t("businessAccount.providerConfig.monthlyLimit") }}
                          </div>
                          <div :id="provider.providerType + '_monthly_limit'" class="text-h6">{{ provider.monthlyLimit }}</div>
                        </v-col>
                        <v-col cols="12" sm="4" class="colSeparator">
                          <div class="yearly_limit caption grey--text text--darken-1">
                            {{ $t("businessAccount.providerConfig.yearlyLimit") }}
                          </div>
                          <div :id="provider.providerType + '_yearly_limit'" class="text-h6">{{ provider.yearlyLimit }}</div>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <div class="yearlyValidUntil caption grey--text text--darken-1">
                            {{ $t("businessAccount.providerConfig.yearlyValidUntil") }}
                          </div>
                          <div class="text-h6">{{ provider.yearlyValidUntil }}</div>
                        </v-col>
                      </v-row>

                      <!-- Total Usage -->
                      <v-row class="mb-2">
                        <v-col cols="12">
                          <div
                            class="pa-3 rounded"
                            style="border: 2px solid var(--v-primary-base)"
                          >
                            <div class="subtitle-2 mb-2 font-weight-bold">
                              {{ $t("businessAccount.providerConfig.totalUsage") }}
                            </div>
                            <v-row dense>
                              <v-col cols="12" sm="6">
                                <div class="caption grey--text text--darken-1">
                                  {{ $t("businessAccount.providerConfig.totalMonthlyCounter") }}
                                </div>
                                <div :id="provider.providerType + '_total_monthly_usage'" class="monthly_total_usage text-h6 primary--text">
                                  {{ provider.currentMonthlyUsage || 0 }}
                                </div>
                              </v-col>
                              <v-col cols="12" sm="6">
                                <div class="caption grey--text text--darken-1">
                                  {{ $t("businessAccount.providerConfig.totalYearlyCounter") }}
                                </div>
                                <div :id="provider.providerType + '_total_yearly_usage'" class="yearly_total_usage text-h6 primary--text">
                                  {{ provider.currentYearlyUsage || 0 }}
                                </div>
                              </v-col>
                            </v-row>
                          </div>
                        </v-col>
                      </v-row>

                      <v-divider class="mb-4"></v-divider>

                      <div class="subtitle-2 font-weight-bold mb-2">
                        {{ $t("businessAccount.providerConfig.usageTitle") }}
                      </div>

                      <v-text-field
                        v-model="providerUsageSearches[provider.id]"
                        :label="$t('businessAccount.providerConfig.search')"
                        prepend-inner-icon="mdi-magnify"
                        clearable
                        dense
                        outlined
                        hide-details
                        class="mb-3"
                        style="max-width: 360px"
                        @input="onProviderUsageSearch(provider)"
                        @click:clear="onProviderUsageSearch(provider)"
                      ></v-text-field>

                      <v-data-table
                        :id="provider.providerType + '_provider_usage_table'"
                        :headers="providerUsageHeaders"
                        :items="providerUsageItems"
                        :server-items-length="providerUsageTotalItems"
                        :loading="providerUsageLoading"
                        :options.sync="providerUsagePagination"
                        class="elevation-1"
                        :no-data-text="$t('businessAccount.providerConfig.noData')"
                        :loading-text="$t('dataTable.content.loadingPleaseWait')"
                        :footer-props="{
                          'items-per-page-text': $t('dataTable.footer.itemsPerPageText'),
                          'page-text': $t('dataTable.footer.pageText'),
                          'items-per-page-options': [5, 10, 25, 50],
                        }"
                      >
                        <template #item.id="{ item }">
                          {{ item.id != null ? item.id : $t('businessAccount.removedUser') }}
                        </template>

                        <template #item.monthlyCounter="{ item }">
                          <v-chip
                            :id="provider.providerType + '_monthly_usage'"
                            small
                            :color="getUsageChipColor(item.monthlyCounter, provider.monthlyLimit)"
                          >
                            {{ item.monthlyCounter }}
                          </v-chip>
                        </template>
                        <template #item.yearlyCounter="{ item }">
                          <v-chip
                            :id="provider.providerType + '_yearly_usage'"
                            small
                            :color="getUsageChipColor(item.yearlyCounter, provider.yearlyLimit)"
                          >
                            {{ item.yearlyCounter }}
                          </v-chip>
                        </template>
                      </v-data-table>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
              </v-tabs-items>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- No BA (non-admin mode only) -->
      <v-row v-if="!businessAccountLoading && !businessAccountDetails && !isAdmin">
        <v-col class="text-center">
          <span class="grey--text">{{ $t('businessAccount.noBusinessAccount') }}</span>
        </v-col>
      </v-row>
    </v-container>

    <!-- Update Limits Dialog (admin only) -->
    <v-dialog id="update_limit_modal_root" v-model="updateDialog" max-width="700" persistent>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-pencil</v-icon>
          {{ $t('businessAccount.admin.updateLimits') }}
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="hasInvalidProviders"
            type="warning"
            dense
            outlined
            class="mb-3"
          >
            {{ $t('businessAccount.admin.invalidProviderWarning') }}
          </v-alert>
          <v-form ref="updateForm" v-model="updateFormValid">
            <div class="subtitle-2 font-weight-bold mb-2">
              {{ $t('businessAccount.admin.providers') }}
            </div>

            <v-card
              v-for="(provider, idx) in updateReq.providers"
              :key="idx"
              outlined
              class="mb-3 pa-3"
            >
              <!-- EXTERNAL: two rows -->
              <template v-if="provider.providerType === 'EXTERNAL'">
                <v-row dense align="center">
                  <v-col cols="12" sm="3">
                    <v-tooltip
                      top
                      :disabled="!isProviderTypeInvalid(provider)"
                      color="error"
                    >
                      <template #activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on">
                          <v-select
                            :id="'provider_type_' + idx"
                            v-model="provider.providerType"
                            :items="availableUpdateProviderTypes(idx)"
                            :label="$t('businessAccount.admin.providerType')"
                            :rules="[v => !!v || $t('validation.form.requiredField')]"
                            :error="isProviderTypeInvalid(provider)"
                            outlined
                            dense
                            hide-details
                            @change="onUpdateProviderTypeChange(provider)"
                          ></v-select>
                        </div>
                      </template>
                      <span>{{ getProviderTypeInvalidTooltip(provider) }}</span>
                    </v-tooltip>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      :id="'provider_kind_' + idx"
                      v-model="provider.providerKind"
                      :label="$t('businessAccount.admin.providerKind')"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="auto" class="ml-auto">
                    <v-btn :id="'remove_provider_' + idx" icon small color="error" @click="removeUpdateProvider(idx)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row dense align="center" class="mt-2">
                  <v-col cols="12" sm="3">
                    <v-text-field
                      :id="'monthly_limit_' + idx"
                      v-model.number="provider.monthlyLimit"
                      :label="$t('businessAccount.admin.monthlyLimit')"
                      type="number"
                      min="0"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      :id="'yearly_limit_' + idx"
                      v-model.number="provider.yearlyLimit"
                      :label="$t('businessAccount.admin.yearlyLimit')"
                      type="number"
                      min="0"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                </v-row>
              </template>

              <!-- Non-EXTERNAL: single row -->
              <v-row v-else dense align="center">
                <v-col cols="12" sm="3">
                  <v-tooltip
                    top
                    :disabled="!isProviderTypeInvalid(provider)"
                    color="error"
                  >
                    <template #activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on">
                        <v-select
                          :id="'provider_type_' + idx"
                          v-model="provider.providerType"
                          :items="availableUpdateProviderTypes(idx)"
                          :label="$t('businessAccount.admin.providerType')"
                          :rules="[v => !!v || $t('validation.form.requiredField')]"
                          :error="isProviderTypeInvalid(provider)"
                          outlined
                          dense
                          hide-details
                          @change="onUpdateProviderTypeChange(provider)"
                        ></v-select>
                      </div>
                    </template>
                    <span>{{ getProviderTypeInvalidTooltip(provider) }}</span>
                  </v-tooltip>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    :id="'monthly_limit_' + idx"
                    v-model.number="provider.monthlyLimit"
                    :label="$t('businessAccount.admin.monthlyLimit')"
                    type="number"
                    min="0"
                    outlined
                    dense
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    :id="'yearly_limit_' + idx"
                    v-model.number="provider.yearlyLimit"
                    :label="$t('businessAccount.admin.yearlyLimit')"
                    type="number"
                    min="0"
                    outlined
                    dense
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="auto">
                  <v-btn :id="'remove_provider_' + idx" icon small color="error" @click="removeUpdateProvider(idx)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>

            <v-btn
              id="add_provider_button"
              text
              color="primary"
              small
              :disabled="updateProviderTypesExhausted"
              @click="addUpdateProvider"
            >
              <v-icon small left>mdi-plus</v-icon>
              {{ $t('businessAccount.admin.addProvider') }}
            </v-btn>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn id="close_modal_button" text @click="updateDialog = false">{{ $t('main.cancel') }}</v-btn>
          <v-btn
            id="submit_create_ba_button"
            color="primary"
            :disabled="!updateFormValid || hasInvalidProviders"
            :loading="updating"
            @click="submitUpdate"
          >
            {{ $t('main.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import businessAccount from '@/services/api/businessAccount'
import seal from '@/services/api/seal'
import credentialRefs from '@/services/api/credentialRefs'
import { showConfirmationMenu, showToastError, showToastSuccess } from '../../event-bus'
import UserGroups from '../users/groups/UserGroups.vue'
import { mapGetters } from 'vuex'
import Role from '../../Role'

export default {
  name: 'BusinessAccount',
  components: { UserGroups },

  props: {
    baId: {
      type: String,
      default: null
    },
    fromAdmin: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      clientName: 'web',
      providers: [],
      evroEnabled: this.$store.state.remoteConfig.evroEnabled
        ? this.$store.state.remoteConfig.evroEnabled
        : false,
      sealStatus: {
        enabled: false,
        secureMode: false,
        activated: false
      },
      alfaEnabled: this.$store.state.remoteConfig.alfaEnabled,
      businessAccountLoading: false,
      businessAccountMembersLoading: false,
      businessAccountDetails: null,
      businessAccountMembers: [],
      membersTotalItems: 0,
      membersPagination: {
        page: 1,
        itemsPerPage: 5,
        sortBy: ['id'],
        sortDesc: [false]
      },
      businessAccountMembersHeaders: [
        { text: this.$t('businessAccount.members.headers.id'), value: 'id' },
        { text: this.$t('businessAccount.members.headers.email'), value: 'email' },
        { text: this.$t('businessAccount.members.headers.firstName'), value: 'firstName' },
        { text: this.$t('businessAccount.members.headers.lastName'), value: 'lastName' },
        { text: this.$t('businessAccount.members.headers.role'), value: 'businessAccountRole' },
        { text: this.$t('businessAccount.members.headers.status'), value: 'businessAccountStatus' },
        { text: this.$t('businessAccount.members.headers.actions'), value: 'actions', sortable: false }
      ],
      newMemberEmail: '',
      newMemberIsAdmin: false,
      addingMember: false,
      deletingMember: false,
      updatingMemberRole: false,
      activeProviderTab: 0,
      providerUsageSearches: {},
      providerUsageHeaders: [
        { text: this.$t('businessAccount.providerConfig.headers.userId'), value: 'id', sortable: false },
        { text: this.$t('businessAccount.providerConfig.headers.email'), value: 'email', sortable: false },
        { text: this.$t('businessAccount.providerConfig.headers.name'), value: 'name', sortable: false },
        { text: this.$t('businessAccount.providerConfig.headers.monthlyCounter'), value: 'monthlyCounter', sortable: false },
        { text: this.$t('businessAccount.providerConfig.headers.yearlyCounter'), value: 'yearlyCounter', sortable: false }
      ],
      providerUsagePagination: { page: 1, itemsPerPage: 10 },
      providerUsageTotalItems: 0,
      providerUsageItems: [],
      providerUsageLoading: false,
      rules: {
        email: (v) =>
          !v ||
          /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) ||
          this.$t('validation.form.emailMustBeValid')
      },
      // Update dialog (admin)
      updateDialog: false,
      updating: false,
      updateFormValid: false,
      updateReq: {
        name: '',
        providers: []
      },
      // Inline name edit
      editName: '',
      savingName: false
    }
  },

  computed: {
    ...mapGetters(['hasRole']),
    isAdmin() {
      return this.hasRole(Role.ADMIN)
    },
    sealActivated() {
      return this.sealStatus.enabled && this.sealStatus.activated
    },

    providerTypeOptions() {
      const options = ['INTERNAL']
      if (this.sealActivated) options.push('CENTRAL')
      if (this.evroEnabled) options.push('EVRO')
      if (this.alfaEnabled) options.push('ALFA')
      if (this.externalProvidersEnabled) options.push('EXTERNAL')
      return options
    },

    externalProvidersEnabled() {
      return this.providers?.length > 0
    },

    updateProviderTypesExhausted() {
      const usedNonExternal = this.updateReq.providers
        .filter(p => p.providerType && p.providerType !== 'EXTERNAL')
        .map(p => p.providerType)
      const availableNonExternal = this.providerTypeOptions.filter(t => t !== 'EXTERNAL')
      const allNonExternalUsed = availableNonExternal.every(t => usedNonExternal.includes(t))
      if (this.externalProvidersEnabled) return false
      return allNonExternalUsed
    },

    hasInvalidProviders() {
      return this.updateReq.providers.some(
        p => !!p.providerType && !this.providerTypeOptions.includes(p.providerType)
      )
    }
  },

  watch: {
    membersPagination: {
      handler() {
        if (!this.businessAccountMembersLoading && this.businessAccountDetails?.id) {
          this.listMembers(this.businessAccountDetails.id)
        }
      }
    },

    providerUsagePagination: {
      handler() {
        if (!this.providerUsageLoading && this.businessAccountDetails) {
          const provider = this.businessAccountDetails.providerLimitConfigs?.[this.activeProviderTab]
          if (provider) {
            this.listProviderUsages(provider)
          }
        }
      },
      deep: true
    },

    activeProviderTab: {
      handler(newTab) {
        this.providerUsageItems = []
        this.providerUsageTotalItems = 0
        this.providerUsagePagination = { page: 1, itemsPerPage: this.providerUsagePagination.itemsPerPage }
      }
    }
  },

  async mounted() {
    this.sealStatus = await seal.getSealStatus()
    this.providers = await credentialRefs.getExternalProviders(this.clientName)
    // console.log('this.sealStatus', this.sealStatus)
    await this.loadInitialData()
  },

  methods: {
    getErrorMessage(error) {
      return error?.response?.data?.message || error?.message || this.$t('toastMessage.errorWhileRetrievingData')
    },

    async loadInitialData() {
      this.businessAccountLoading = true
      try {
        if (this.isAdmin && this.baId) {
          this.businessAccountLoading = false
          await this.loadBusinessAccountDetails(this.baId)
        } else {
          const pageRequest = { 'page-size': 5, 'page-num': 1 }
          const response = await businessAccount.listMyBusinessAccounts(pageRequest)
          if (response.content && response.content.length > 0) {
            const id = response.content[0].id
            this.businessAccountLoading = false
            await this.loadBusinessAccountDetails(id)
          }
        }
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      } finally {
        this.businessAccountLoading = false
      }
    },

    async loadBusinessAccountDetails(id) {
      if (this.businessAccountLoading) return
      this.businessAccountLoading = true
      try {
        let details
        if (this.isAdmin) {
          details = await businessAccount.getBusinessAccount(id)
        } else {
          details = await businessAccount.getMyBusinessAccountAdv(id)
        }
        this.businessAccountDetails = details
        this.editName = details.name
        await this.listMembers(id)
        this.providerUsageItems = []
        this.providerUsageTotalItems = 0
        this.providerUsagePagination = { page: 1, itemsPerPage: this.providerUsagePagination.itemsPerPage }
        this.activeProviderTab = 0
        const firstProvider = details.providerLimitConfigs?.[0]
        if (firstProvider) {
          await this.listProviderUsages(firstProvider)
        }
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg || this.$t('businessAccount.detailsLoadFailed'))
      } finally {
        this.businessAccountLoading = false
      }
    },

    async listMembers(id) {
      this.businessAccountMembersLoading = true
      try {
        const pageRequest = {
          'page-num': this.membersPagination.page,
          'page-size': this.membersPagination.itemsPerPage || 10,
          'sort-field': this.membersPagination.sortBy[0],
          'sort-order': this.membersPagination.sortDesc[0] ? 'desc' : 'asc'
        }
        const membersResp = await businessAccount.listMembers(id, undefined, pageRequest)
        this.businessAccountMembers = membersResp.content
        this.membersTotalItems = membersResp.total
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      } finally {
        this.businessAccountMembersLoading = false
      }
    },

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    },

    async addMember() {
      if (!this.newMemberEmail || !this.isValidEmail(this.newMemberEmail)) {
        showToastError(this.$t('businessAccount.members.invalidEmail'))
        return
      }
      this.addingMember = true
      try {
        const req = { email: this.newMemberEmail, isAdmin: this.newMemberIsAdmin }
        await businessAccount.inviteMember(this.businessAccountDetails.id, req)
        await this.loadBusinessAccountDetails(this.businessAccountDetails.id)
        showToastSuccess(this.$t('businessAccount.members.memberAdded'))
        this.newMemberEmail = ''
        this.newMemberIsAdmin = false
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.addingMember = false
      }
    },

    async deleteMember(member, e) {
      const opts = { title: this.$t('dialog.confirmation.title'), posX: e.clientX, posY: e.clientY }
      const conf = await showConfirmationMenu(opts)
      if (!conf) return
      this.deletingMember = true
      try {
        await businessAccount.removeMember(this.businessAccountDetails.id, member.id)
        await this.loadBusinessAccountDetails(this.businessAccountDetails.id)
        showToastSuccess(this.$t('businessAccount.members.memberDeleted'))
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.deletingMember = false
      }
    },

    async toggleMemberRole(member) {
      if (member.businessAccountStatus === 'INVITED') return
      this.updatingMemberRole = true
      try {
        const newIsAdmin = member.businessAccountRole !== 'ADMIN'
        await businessAccount.updateMember(this.businessAccountDetails.id, member.id, { isAdmin: newIsAdmin })
        await this.loadBusinessAccountDetails(this.businessAccountDetails.id)
        const successMsg = newIsAdmin
          ? this.$t('businessAccount.members.roleChangedToAdmin')
          : this.$t('businessAccount.members.roleChangedToMember')
        showToastSuccess(successMsg)
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.updatingMemberRole = false
      }
    },

    openUpdateDialog() {
      const configs = this.businessAccountDetails.providerLimitConfigs || []
      this.updateReq = {
        providers: configs.map(c => ({
          providerType: c.providerType,
          providerKind: c.providerKind || '',
          monthlyLimit: c.monthlyLimit,
          yearlyLimit: c.yearlyLimit
        }))
      }
      this.updateDialog = true
      this.$nextTick(() => {
        if (this.$refs.updateForm) this.$refs.updateForm.resetValidation()
      })
    },

    async submitNameUpdate() {
      if (!this.editName || this.editName === this.businessAccountDetails.name) return
      this.savingName = true
      try {
        const configs = this.businessAccountDetails.providerLimitConfigs || []
        const providers = configs.map(c => {
          const obj = { providerType: c.providerType, monthlyLimit: c.monthlyLimit, yearlyLimit: c.yearlyLimit }
          if (c.providerKind) obj.providerKind = c.providerKind
          return obj
        })
        await businessAccount.updateBusinessAccount(this.businessAccountDetails.id, {
          name: this.editName,
          providers
        })
        showToastSuccess(this.$t('businessAccount.admin.updateSuccess'))
        await this.loadBusinessAccountDetails(this.businessAccountDetails.id)
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.savingName = false
      }
    },

    isProviderTypeInvalid(provider) {
      return !!provider.providerType && !this.providerTypeOptions.includes(provider.providerType)
    },

    getProviderTypeInvalidTooltip(provider) {
      if (!this.isProviderTypeInvalid(provider)) return ''
      return this.$t('businessAccount.admin.invalidProviderType', { type: provider.providerType })
    },

    availableUpdateProviderTypes(currentIdx) {
      const currentType = this.updateReq.providers[currentIdx]?.providerType
      const usedNonExternal = this.updateReq.providers
        .filter((p, i) => i !== currentIdx && p.providerType && p.providerType !== 'EXTERNAL')
        .map(p => p.providerType)
      const items = this.providerTypeOptions.map(t => ({
        text: t,
        value: t,
        disabled: t !== 'EXTERNAL' && usedNonExternal.includes(t)
      }))
      // If the current value is not in providerTypeOptions (invalid/outdated), still include it so v-select can display it
      if (currentType && !this.providerTypeOptions.includes(currentType)) {
        items.unshift({
          text: currentType + ' ⚠',
          value: currentType,
          disabled: false
        })
      }
      return items
    },

    onUpdateProviderTypeChange(provider) {
      if (provider.providerType !== 'EXTERNAL') {
        provider.providerKind = undefined
      } else {
        provider.providerKind = ''
      }
    },

    addUpdateProvider() {
      this.updateReq.providers.push({
        providerType: '',
        providerKind: '',
        monthlyLimit: 0,
        yearlyLimit: 0
      })
    },

    removeUpdateProvider(idx) {
      this.updateReq.providers.splice(idx, 1)
    },

    async submitUpdate() {
      if (!this.$refs.updateForm.validate()) return
      this.updating = true
      try {
        const providers = this.updateReq.providers.map(p => {
          const obj = {
            providerType: p.providerType,
            monthlyLimit: p.monthlyLimit,
            yearlyLimit: p.yearlyLimit
          }
          if (p.providerKind) obj.providerKind = p.providerKind
          return obj
        })
        await businessAccount.updateBusinessAccount(this.businessAccountDetails.id, {
          name: this.editName,
          providers
        })
        showToastSuccess(this.$t('businessAccount.admin.updateSuccess'))
        this.updateDialog = false
        await this.loadBusinessAccountDetails(this.businessAccountDetails.id)
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.updating = false
      }
    },

    async listProviderUsages(provider) {
      this.providerUsageLoading = true
      try {
        const pageRequest = {
          'page-num': this.providerUsagePagination.page,
          'page-size': this.providerUsagePagination.itemsPerPage
        }
        const q = this.providerUsageSearches[provider.id] || undefined
        const resp = await businessAccount.listBusinessAccountProviderUsages(
          this.businessAccountDetails.id,
          provider.id,
          q,
          pageRequest
        )
        this.providerUsageItems = (resp.content || []).map(item => ({
          id: item.usageBy?.id,
          email: item.usageBy?.email,
          name: `${item.usageBy?.firstName || ''} ${item.usageBy?.lastName || ''}`.trim(),
          monthlyCounter: item.monthlyCounter,
          yearlyCounter: item.yearlyCounter
        }))
        this.providerUsageTotalItems = resp.total || 0
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.providerUsageLoading = false
      }
    },

    onProviderUsageSearch(provider) {
      clearTimeout(this._providerSearchTimer)
      this._providerSearchTimer = setTimeout(() => {
        this.providerUsagePagination = { ...this.providerUsagePagination, page: 1 }
        this.listProviderUsages(provider)
      }, 400)
    },

    getUsageChipColor(counter, limit) {
      if (!limit || limit === 0) return 'default'
      const ratio = counter / limit
      if (ratio >= 1) return 'error'
      if (ratio >= 0.8) return 'warning'
      return 'success'
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}.${month}.${day}, ${hours}:${minutes}:${seconds}`
    }
  }
}
</script>
<style scoped>
.colSeparator {
  border-right: 0px;
  border-top: 0px;
  border-bottom: 0px;
  border-left: 1px;
  border-style: solid;
  border-color: lightgray;
}
</style>
